import React, { createContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PropTypes from 'prop-types';
import mixpanel from 'mixpanel-browser';

import { supabase } from '../lib/supabase'; // Singleton supabase client
import { apiClient } from '../lib/apiClient';
import { useTheme } from '../hooks/useTheme';
import { useCrypto } from '../hooks/useCrypto.ts';
import { appStorage, StorageKeys } from '../lib/appStorage';
import { clearMasterKey } from '../lib/crypto/sessionKeys';
import { isElectron } from '../utils/platform';

import type { Message, UserMessage, AssistantMessage } from '../types/message.ts';
import { Role, VALID_ASSISTANT_PART_TYPES } from '../types/message.ts';
import type { Tab, PersistedTab } from '../types/tab.ts';
import { MAX_TABS } from '../lib/constants.ts';

interface MessagesContextType {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

const AppContext = createContext(null);
const MessagesContext = createContext<MessagesContextType>({
  messages: [],
  setMessages: () => {},
})

export { AppContext, MessagesContext };

export const AppProvider = ({ children }) => {
  // Theme management
  const themeControls = useTheme();

  // Encryption
  const { isUnlocked, decrypt, decryptObject } = useCrypto();

  // Navigation + routing intents
  const navigate = useNavigate();
  const isNewConversationIntent = useRef();

  // Models
  const [models, setModels] = useState([]); // All available models (filtered by API keys)
  const [selectedModel, _setSelectedModel] = useState(null);
  const [enabledModelIds, setEnabledModelIds] = useState([]); // User's enabled model IDs
  const [favoriteModelId, setFavoriteModelId] = useState(null); // User's favorite model ID

  // System prompt for agentic features
  const [systemPrompt, setSystemPrompt] = useState('');

  // Web search preference
  const [preferAnthropicWebSearch, setPreferAnthropicWebSearch] = useState(true);

  // Derived state: Filter models by enabled_model_ids
  const enabledModels = useMemo(() => {
    if (!enabledModelIds || enabledModelIds.length === 0) {
      // If no preferences set, show all models
      return models;
    }
    return models.filter(m => enabledModelIds.includes(m.id));
  }, [models, enabledModelIds]);

  // Initialize or correct selectedModel based on enabledModels and favoriteModelId
  useEffect(() => {
    if (enabledModels.length === 0) return;

    // If we don't have a selection yet, choose favorite or random
    if (!selectedModel) {
      const favorite = favoriteModelId
        ? enabledModels.find(m => m.id === favoriteModelId)
        : null;

      _setSelectedModel(
        favorite || enabledModels[Math.floor(Math.random() * enabledModels.length)]
      );
      return;
    }

    // If current selection became disabled, choose favorite or random
    const stillEnabled = enabledModels.some(m => m.id === selectedModel.id);
    if (!stillEnabled) {
      const favorite = favoriteModelId
        ? enabledModels.find(m => m.id === favoriteModelId)
        : null;

      _setSelectedModel(
        favorite || enabledModels[Math.floor(Math.random() * enabledModels.length)]
      );
    }
  }, [enabledModels, favoriteModelId, selectedModel]);

  // Authentication, API keys, profile
  const [authSession, setAuthSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // Track initial auth loading

  const [apiKeys, setApiKeys] = useState({
    anthropicApiKey: '',
    openAIApiKey: '',
    geminiApiKey: '',
    xaiApiKey: '',
    openrouterApiKey: '',
  });
  const [profile, setProfile] = useState(null);

  // UI State
  const [thinkingMode, setThinkingMode] = useState('medium');
  const [menuIsOpen, setMenuIsOpen] = useState(() => {
    // Open by default on desktop (>= 768px), closed on mobile
    return typeof window !== 'undefined' && window.innerWidth >= 768;
  });
  const [searchModalActive, setSearchModalActive] = useState(false);
  const [input, setInput] = useState('');
  const [showDesk, setShowDesk] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeConversationTokenCount, setActiveConversationTokenCount] = useState(0);
  const [initializingUI, setInitializingUI] = useState(true);

  // Confirmation Modal State
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'default',
    onConfirm: null,
    onCancel: null,
    isLoading: false,
  });

  // File uploads
  const [files, setFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState({});

  // Conversations
  const [conversations, setConversations] = useState([]);
  const [favoriteConversations, setFavoriteConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(() => ({
    conversationId: null,
    history: [],
  }));
  const [conversationsLoading, setConversationsLoading] = useState(true);

  // Tabs - Multi-tab conversation system
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const tabsInitializedRef = useRef(false);
  const saveTabsTimeoutRef = useRef<number | null>(null);
  const tabsRef = useRef<Tab[]>(tabs);
  tabsRef.current = tabs;

  // Keep refs synchronized with state to avoid stale closures and dependency churn
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const favoriteConversationsRef = useRef(favoriteConversations);
  favoriteConversationsRef.current = favoriteConversations;
  const selectedConversationRef = useRef(selectedConversation);
  selectedConversationRef.current = selectedConversation;

  const searchConversations = useCallback(async (query) => {
    try {
      const results = await apiClient.post('/api/conversations/search', {
        query: query,
      });

      const processedConversations = results.map((serverConv) => ({
        conversationId: String(serverConv.conversation_id),
        title: serverConv.conversation_title,
        matchedMsg: serverConv.matched_message,
        favorite: serverConv.is_favorite,
        updatedAt: serverConv.last_updated,
      }));

      return processedConversations;
    } catch (error) {
      toast('Error searching for conversations');
      return [];
    }
  }, []);

  // Tab management functions
  const getActiveTab = useCallback((): Tab | null => {
    if (!activeTabId) return null;
    return tabs.find(t => t.id === activeTabId) || null;
  }, [tabs, activeTabId]);

  const createTab = useCallback((conversationId: string | null = null, initialModelId: string | null = null): string | null => {
    if (tabsRef.current.length >= MAX_TABS) {
      toast.error(`Maximum of ${MAX_TABS} tabs reached`);
      return null;
    }
    const tabId = crypto.randomUUID();
    const newTab: Tab = {
      id: tabId,
      conversationId,
      messages: [],
      input: '',
      files: [],
      selectedModelId: initialModelId || selectedModel?.id || null,
      thinkingMode,
      systemPrompt,
      isGenerating: false,
      activeConversationTokenCount: 0,
      createdAt: Date.now(),
      title: conversationId ? conversationsRef.current.find(c => c.conversationId === conversationId)?.title || favoriteConversationsRef.current.find(c => c.conversationId === conversationId)?.title : 'New Chat',
    };

    setTabs(prev => [...prev, newTab]);
    setActiveTabId(tabId);
    return tabId;
  }, [selectedModel, thinkingMode, systemPrompt]);

  const closeTab = useCallback((tabId: string) => {
    setTabs(prev => {
      const filtered = prev.filter(t => t.id !== tabId);
      
      // If closing active tab, switch to another
      if (activeTabId === tabId) {
        if (filtered.length > 0) {
          // Switch to the tab that was before this one, or the first tab
          const currentIndex = prev.findIndex(t => t.id === tabId);
          const newActiveIndex = currentIndex > 0 ? currentIndex - 1 : 0;
          setActiveTabId(filtered[newActiveIndex]?.id || null);
        } else {
          // No tabs left, create a new one immediately
          const newTabId = crypto.randomUUID();
          const newTab: Tab = {
            id: newTabId,
            conversationId: null,
            messages: [],
            input: '',
            files: [],
            selectedModelId: selectedModel?.id || null,
            thinkingMode,
            systemPrompt,
            isGenerating: false,
            activeConversationTokenCount: 0,
            createdAt: Date.now(),
            title: 'New Chat',
          };
          setActiveTabId(newTabId);
          return [newTab];
        }
      }

      return filtered;
    });
  }, [activeTabId, selectedModel, thinkingMode, systemPrompt]);

  const switchTab = useCallback((tabId: string) => {
    if (tabsRef.current.some(t => t.id === tabId)) {
      setActiveTabId(tabId);
    }
  }, []);

  const updateTab = useCallback((tabId: string, updates: Partial<Tab>) => {
    setTabs(prev => prev.map(t => 
      t.id === tabId ? { ...t, ...updates } : t
    ));
  }, []);

  const closeOtherTabs = useCallback((keepTabId: string) => {
    setTabs(prev => prev.filter(t => t.id === keepTabId));
    setActiveTabId(keepTabId);
  }, []);

  const closeAllTabs = useCallback(() => {
    const tabId = crypto.randomUUID();
    const newTab: Tab = {
      id: tabId,
      conversationId: null,
      messages: [],
      input: '',
      files: [],
      selectedModelId: selectedModel?.id || null,
      thinkingMode,
      systemPrompt,
      isGenerating: false,
      activeConversationTokenCount: 0,
      createdAt: Date.now(),
      title: 'New Chat',
    };
    setTabs([newTab]);
    setActiveTabId(tabId);
  }, [selectedModel, thinkingMode, systemPrompt]);

  const reorderTabs = useCallback((fromIndex: number, toIndex: number) => {
    setTabs(prev => {
      const sorted = [...prev].sort((a, b) => a.createdAt - b.createdAt);
      const [moved] = sorted.splice(fromIndex, 1);
      sorted.splice(toIndex, 0, moved);
      // Reassign createdAt to maintain the new visual order
      return sorted.map((tab, i) => ({ ...tab, createdAt: i }));
    });
  }, []);

  // Refs for tracking fetch status
  const conversationsFetchedRef = useRef(false);
  const modelsFetchedRef = useRef(false);
  const prevAuthSessionRef = useRef(null);
  const apikeysFetchedRef = useRef(false);
  const profileFetchedRef = useRef(false);

  // API Keys update handler
  const onApiKeysUpdate = useCallback((anthropicApiKey, openAIApiKey, geminiApiKey, xaiApiKey, openrouterApiKey) => {
    setApiKeys({
      anthropicApiKey: anthropicApiKey ?? '',
      openAIApiKey: openAIApiKey ?? '',
      geminiApiKey: geminiApiKey ?? '',
      xaiApiKey: xaiApiKey ?? '',
      openrouterApiKey: openrouterApiKey ?? '',
    });
  }, []);

  // Initialize app data after authentication
  const initializeAfterAuth = useCallback(async (session) => {
    setInitializingUI(true);
    const token = session.access_token;

    const tasks = [];

    // Fetch conversations
    if (!conversationsFetchedRef.current) {
      tasks.push((async () => {
        conversationsFetchedRef.current = true;
        try {
          const serverConversations = await apiClient.get('/api/conversations', {}, token);
          const formattedConversations = await Promise.all(serverConversations.map(async (serverConv) => {
            let title = serverConv.title;
            if (serverConv.encrypted) {
              if (isUnlocked) {
                try {
                  title = await decrypt(serverConv.title);
                } catch {
                  title = 'Encrypted conversation';
                }
              } else {
                title = 'Encrypted conversation';
              }
            }
            return {
              conversationId: String(serverConv.id),
              history: [],
              title,
              favorite: serverConv.favorite,
              metadata: serverConv.metadata,
              encrypted: serverConv.encrypted || false,
              createdAt: serverConv.created_at,
              updatedAt: serverConv.updated_at,
            };
          }));

          const [favorites, standard] = formattedConversations.reduce(
            ([favorites, standard], c) => {
              c.favorite ? favorites.push(c) : standard.push(c);
              return [favorites, standard];
            },
            [[], []]
          );

          setFavoriteConversations(favorites);
          setConversations(standard);
          setConversationsLoading(false);
        } catch (error) {
          conversationsFetchedRef.current = false;
          setConversationsLoading(false);
          console.error('Conversations fetch error:', error);
        }
      })());
    }

    // Fetch models
    if (!modelsFetchedRef.current) {
      tasks.push((async () => {
        modelsFetchedRef.current = true;
        try {
          const fetchedModels = await apiClient.get('/api/models', {}, token);
          setModels(fetchedModels);
          return fetchedModels;
        } catch (error) {
          modelsFetchedRef.current = false;
          console.error('Models fetch error:', error);
          return null;
        }
      })());
    }

    // Fetch API keys and model preferences
    if (!apikeysFetchedRef.current) {
      tasks.push((async () => {
        apikeysFetchedRef.current = true;
        try {
          const fetchedSettings = await apiClient.get('/api/user-settings', {}, token);
          setApiKeys({
            anthropicApiKey: fetchedSettings.anthropic_apikey ?? '',
            openAIApiKey: fetchedSettings.openai_apikey ?? '',
            geminiApiKey: fetchedSettings.gemini_apikey ?? '',
            xaiApiKey: fetchedSettings.xai_apikey ?? '',
            openrouterApiKey: fetchedSettings.openrouter_apikey ?? '',
          });
          setEnabledModelIds(fetchedSettings.enabled_model_ids || []);
          setFavoriteModelId(fetchedSettings.favorite_model_id || null);
          setPreferAnthropicWebSearch(fetchedSettings.prefer_anthropic_web_search ?? true);
          return fetchedSettings;
        } catch (error) {
          apikeysFetchedRef.current = false;
          console.error('Settings fetch error:', error);
          return null;
        }
      })());
    }

    // Fetch profile
    if (!profileFetchedRef.current) {
      tasks.push((async () => {
        profileFetchedRef.current = true;
        try {
          const data = await apiClient.get('/api/profile', {}, token);
          setProfile(data);
          
          // Set user properties in Mixpanel
          if (import.meta.env.VITE_MIXPANEL_TOKEN) {
            mixpanel.people.set({
              $email: data.email,
              $name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
              $first_name: data.first_name,
              $last_name: data.last_name,
              has_completed_onboarding: data.has_completed_onboarding,
            });
          }
        } catch (error) {
          profileFetchedRef.current = false;
          console.error('Profile fetch error:', error);
        }
      })());
    }

    // Wait for all fetches to complete
    const results = await Promise.allSettled(tasks);
    
    // Handle initial model selection
    const modelsResult = results.find((r, i) => tasks[i].toString().includes('models'));
    const settingsResult = results.find((r, i) => tasks[i].toString().includes('user-settings'));
    
    if (modelsResult?.status === 'fulfilled' && settingsResult?.status === 'fulfilled') {
      const fetchedModels = await modelsResult.value;
      const fetchedSettings = await settingsResult.value;
      
      if (fetchedModels && fetchedSettings) {
        const userEnabledIds = fetchedSettings.enabled_model_ids || [];
        const userFavoriteId = fetchedSettings.favorite_model_id;
        
        const availableModels = userEnabledIds.length > 0
          ? fetchedModels.filter(m => userEnabledIds.includes(m.id))
          : fetchedModels;
        
        if (userFavoriteId && availableModels.length > 0) {
          const favoriteModel = availableModels.find(m => m.id === userFavoriteId);
          if (favoriteModel) {
            _setSelectedModel(favoriteModel);
          } else {
            const randomModel = availableModels[Math.floor(Math.random() * availableModels.length)];
            _setSelectedModel(randomModel);
          }
        } else if (availableModels.length > 0) {
          const randomModel = availableModels[Math.floor(Math.random() * availableModels.length)];
          _setSelectedModel(randomModel);
        }
      }
    }

    setInitializingUI(false);
  }, [isUnlocked, decrypt]);

  // Function to fetch models
  const fetchModels = useCallback(async () => {
    try {
      const data = await apiClient.get('/api/models');
      setModels(data);
      if (data.length > 0) {
        _setSelectedModel((prev) => prev || data[0]); // Use functional update
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      throw error;
    }
  }, []);

  // Function to fetch profile
  const fetchProfile = useCallback(async () => {
    try {
      const data = await apiClient.get('/api/profile');
      setProfile(data);
    } catch (err) {
      console.error(err.message);
    }
  }, []);

  // Function to fetch user settings (model preferences)
  const fetchUserSettings = useCallback(async () => {
    try {
      const settings = await apiClient.get('/api/user-settings');
      setEnabledModelIds(settings.enabled_model_ids || []);
      setFavoriteModelId(settings.favorite_model_id || null);
    } catch (err) {
      console.error('Error fetching user settings:', err);
      // On error, default to showing all models
      setEnabledModelIds([]);
      setFavoriteModelId(null);
    }
  }, []);

  // Function to sync conversations with server
  const syncConversationsWithServer = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const serverConversations = await apiClient.get('/api/conversations');
      const formattedConversations = await Promise.all(serverConversations.map(async (serverConv) => {
        const createdAtMs = Date.parse(serverConv.created_at);
        const updatedAtMs = Date.parse(serverConv.updated_at);

        let title = serverConv.title;
        if (serverConv.encrypted) {
          if (isUnlocked) {
            try {
              title = await decrypt(serverConv.title);
            } catch {
              title = 'Encrypted conversation';
            }
          } else {
            title = 'Encrypted conversation';
          }
        }

        return {
          conversationId: String(serverConv.id),
          history: [],
          title,
          favorite: serverConv.favorite,
          metadata: serverConv.metadata,
          encrypted: serverConv.encrypted || false,
          createdAt: serverConv.created_at,
          updatedAt: serverConv.updated_at,
          createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : 0,
          updatedAtMs: Number.isFinite(updatedAtMs) ? updatedAtMs : 0,
        };
      }));

      const [favorites, standard] = formattedConversations.reduce(
        ([favs, std], c) => {
          (c.favorite ? favs : std).push(c);
          return [favs, std];
        },
        [[], []]
      );

      setFavoriteConversations(favorites);
      setConversations(standard);
    } catch (error) {
      console.error('Error syncing conversations with server:', error);
      throw error;
    } finally {
      setConversationsLoading(false);
    }
  }, [isUnlocked, decrypt]);

  const renameTab = useCallback(async (tabId: string, title: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    const previousTitle = tab.title;

    setTabs(prev => prev.map(t => 
      t.id === tabId ? { ...t, title } : t
    ));

    if (tab.conversationId) {
      try {
        await apiClient.put(`/api/conversations/${tab.conversationId}/title`, { title });
        await syncConversationsWithServer();
      } catch (error) {
        console.error('Error updating conversation title:', error);
        setTabs(prev => prev.map(t => 
          t.id === tabId ? { ...t, title: previousTitle } : t
        ));
        toast.error('Could not rename conversation. Please try again.');
      }
    }
  }, [tabs, syncConversationsWithServer]);

  const deleteConversation = useCallback(
    async (conversationId) => {
      try {
        await apiClient.delete(`/api/conversations/${conversationId}`);
        await syncConversationsWithServer();

        setSelectedConversation((prev) =>
          prev?.conversationId === conversationId
            ? { conversationId: null, title: '', history: [], favorite: false }
            : prev
        );

        return true;
      } catch (error) {
        console.error(error);
        console.error('Error deleting conversation with conversation: ', conversationId);
        return false;
      }
    },
    [syncConversationsWithServer]
  );

  const deleteAllConversations = useCallback(async () => {
    try {
      await apiClient.delete('/api/conversations');
      await syncConversationsWithServer();

      setSelectedConversation({
        conversationId: null,
        title: '',
        history: [],
        favorite: false,
      });

      return true;
    } catch (error) {
      console.error('Error deleting all conversations');
      return false;
    }
  }, [syncConversationsWithServer]);

  const updateTitle = useCallback(
    async (conversationId, title) => {
      try {
        await apiClient.put(`/api/conversations/${conversationId}/title`, { title });
        await syncConversationsWithServer();
        return true;
      } catch (error) {
        console.error('Error updating conversation title:', conversationId, error);
        return false;
      }
    },
    [syncConversationsWithServer]
  );

  const favoriteConversation = useCallback(async (conversationId) => {
    const currentConversations = conversationsRef.current;
    const currentFavorites = favoriteConversationsRef.current;
    const currentSelected = selectedConversationRef.current;

    let conversation =
      currentConversations.find((c) => c.conversationId === conversationId) ||
      currentFavorites.find((c) => c.conversationId === conversationId) ||
      (currentSelected?.conversationId === conversationId ? currentSelected : null);

    if (!conversation && currentSelected?.conversationId === conversationId) {
      conversation = {
        ...currentSelected,
        favorite: currentSelected.favorite ?? false,
      };
    }
    if (!conversation) {
      console.error('Conversation not found:', conversationId);
      return false;
    }

    const newFavoriteStatus = !conversation.favorite;

    try {
      await apiClient.put(`/api/conversations/${conversationId}/favorite`, { favorite: newFavoriteStatus });

      if (newFavoriteStatus) {
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
        setFavoriteConversations((prev) => [...prev, { ...conversation, favorite: true }]);
      } else {
        setFavoriteConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
        setConversations((prev) => [...prev, { ...conversation, favorite: false }]);
      }

      setSelectedConversation((prev) =>
        prev?.conversationId === conversationId ? { ...prev, favorite: newFavoriteStatus } : prev
      );

      toast.success(newFavoriteStatus ? 'Conversation added to favorites' : 'Conversation removed from favorites');
      return true;
    } catch (error) {
      console.error('Error favoriting conversation with conversation:', conversationId, error);
      return false;
    }
  }, []);

  // Listen for auth changes ONCE and clean up
  useEffect(() => {
    if (!supabase) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setAuthSession(session);
      setAuthLoading(false);

      // Redirect to password reset page when user arrives via reset email link
      if (event === 'PASSWORD_RECOVERY' && session) {
        navigate('/reset-password');
        prevAuthSessionRef.current = session;
        return;
      }

      if (
        session &&
        (!prevAuthSessionRef.current || session.access_token !== prevAuthSessionRef.current.access_token)
      ) {
        // Identify user in Mixpanel
        if (import.meta.env.VITE_MIXPANEL_TOKEN && session.user?.id) {
          mixpanel.identify(session.user.id);
        }
        
        // Initialize app data with new session
        await initializeAfterAuth(session);
      } else if (!session) {
        // Logged out - clear state and reset Mixpanel
        if (import.meta.env.VITE_MIXPANEL_TOKEN) {
          mixpanel.reset();
        }

        // Clear persistent storage (tabs, preferences) and encryption keys
        await appStorage.clear();
        await clearMasterKey();
        
        setModels([]);
        _setSelectedModel(null);
        setEnabledModelIds([]);
        setFavoriteModelId(null);
        setApiKeys({ anthropicApiKey: '', openAIApiKey: '', geminiApiKey: '', xaiApiKey: '' });
        setConversations([]);
        setSelectedConversation({ conversationId: null, history: [] });
        setMessages([]);
        setSearchModalActive(false);
        setConversationsLoading(false);
        setProfile(null);
        setInitializingUI(false);
        setTabs([]);
        setActiveTabId(null);

        conversationsFetchedRef.current = false;
        modelsFetchedRef.current = false;
        apikeysFetchedRef.current = false;
        profileFetchedRef.current = false;
        tabsInitializedRef.current = false;
      }

      prevAuthSessionRef.current = session;
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [initializeAfterAuth]);

  // Initialize app data when authSession becomes valid
  // This handles the case where onAuthSessionUpdate is called (e.g., from App.jsx)
  // but the onAuthStateChange listener hasn't fired or missed the event
  useEffect(() => {
    if (!authSession?.access_token) return;
    
    // Only initialize if data hasn't been fetched yet
    const needsInit = !modelsFetchedRef.current || !conversationsFetchedRef.current;
    if (!needsInit) return;

    initializeAfterAuth(authSession);
  }, [authSession, initializeAfterAuth]);

  // Re-decrypt conversations when crypto vault unlocks after initial load
  useEffect(() => {
    if (!isUnlocked) return;
    if (!conversationsFetchedRef.current) return;
    syncConversationsWithServer().catch(() => {});
  }, [isUnlocked]);

  // Create initial tab when authenticated but no tabs exist, restoring from storage if available
  // Tabs are only used in Electron — browser uses legacy global state
  useEffect(() => {
    if (!isElectron()) return;
    if (!authSession?.access_token) return;
    if (tabsInitializedRef.current) return;
    if (tabs.length > 0) {
      tabsInitializedRef.current = true;
      return;
    }

    // Async function to restore or create tabs
    const initializeTabs = async () => {
      try {
        const [storedTabs, storedActiveTabId] = await Promise.all([
          appStorage.getItem<PersistedTab[]>(StorageKeys.TABS),
          appStorage.getItem<string>(StorageKeys.ACTIVE_TAB_ID),
        ]);
        
        if (storedTabs && storedTabs.length > 0) {
          // Restore tabs from lightweight persisted format
          // Add back volatile/runtime fields with defaults
          // Messages will be loaded from server when tab activates (if it has conversationId)
          const restoredTabs: Tab[] = storedTabs.map(t => ({
            id: t.id,
            conversationId: t.conversationId,
            selectedModelId: t.selectedModelId,
            thinkingMode: t.thinkingMode,
            systemPrompt: t.systemPrompt,
            createdAt: t.createdAt,
            title: t.title,
            // Restore volatile/runtime fields with defaults
            messages: [],
            input: '',
            files: [],
            isGenerating: false,
            activeConversationTokenCount: 0,
          }));
          setTabs(restoredTabs);
          
          // Restore active tab if valid, otherwise use first tab
          const validActiveId = storedActiveTabId && restoredTabs.some(t => t.id === storedActiveTabId);
          setActiveTabId(validActiveId ? storedActiveTabId : restoredTabs[0].id);
          tabsInitializedRef.current = true;
          return;
        }
      } catch (e) {
        console.warn('Failed to restore tabs from storage:', e);
      }

      // Create a fresh blank tab if no stored tabs
      const tabId = crypto.randomUUID();
      const newTab: Tab = {
        id: tabId,
        conversationId: null,
        messages: [],
        input: '',
        files: [],
        selectedModelId: selectedModel?.id || null,
        thinkingMode,
        systemPrompt,
        isGenerating: false,
        activeConversationTokenCount: 0,
        createdAt: Date.now(),
        title: 'New Chat',
      };
      setTabs([newTab]);
      setActiveTabId(tabId);
      tabsInitializedRef.current = true;
    };

    initializeTabs();
    // Only depends on auth and whether tabs exist — model/temp/etc are read at creation time via refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession?.access_token, tabs.length]);

  // Tab-aware model setter: persist selection into the active tab (Electron only)
  const activeTabIdRef = useRef<string | null>(activeTabId);
  activeTabIdRef.current = activeTabId;

  const setSelectedModel = useCallback((model) => {
    _setSelectedModel(model);
    if (!isElectron() || !activeTabIdRef.current) return;
    setTabs(prev => prev.map(t =>
      t.id === activeTabIdRef.current
        ? { ...t, selectedModelId: model?.id ?? null }
        : t
    ));
  }, []);
  // Restore global model selection from the active tab when switching tabs (Electron only)
  const isRestoringTabRef = useRef(false);
  useEffect(() => {
    if (!isElectron()) return;
    const tab = tabs.find(t => t.id === activeTabId);
    if (!tab) return;

    isRestoringTabRef.current = true;

    // Restore model
    if (enabledModels.length > 0 && tab.selectedModelId) {
      const desired = enabledModels.find(m => m.id === tab.selectedModelId);
      if (desired && desired.id !== selectedModel?.id) {
        _setSelectedModel(desired);
      }
    }

    isRestoringTabRef.current = false;
    // Only react to tab switches, not to every tabs/model change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Serialize tab to lightweight persistence format (excludes heavy/volatile fields)
  const serializeTabForStorage = useCallback((t: Tab): PersistedTab => ({
    id: t.id,
    conversationId: t.conversationId,
    selectedModelId: t.selectedModelId,
    thinkingMode: t.thinkingMode,
    systemPrompt: t.systemPrompt,
    createdAt: t.createdAt,
    title: t.title,
    // Explicitly omit: messages, files, isGenerating, activeConversationTokenCount, input
    // These are either volatile (streaming state), non-serializable (File objects),
    // or can be fetched from server (messages with conversationId)
  }), []);

  // Sync tab titles when conversations are refreshed from server (Electron only)
  useEffect(() => {
    if (!isElectron()) return;
    const allConversations = [...conversations, ...favoriteConversations];
    if (allConversations.length === 0) return;

    setTabs(prev => {
      if (prev.length === 0) return prev;

      let hasChanges = false;
      const updated = prev.map(tab => {
        if (!tab.conversationId) return tab;
        const conv = allConversations.find(c => c.conversationId === tab.conversationId);
        if (conv?.title && conv.title !== tab.title) {
          hasChanges = true;
          return { ...tab, title: conv.title };
        }
        return tab;
      });

      return hasChanges ? updated : prev;
    });
  }, [conversations, favoriteConversations]);

  // Persist tabs to storage when they change (debounced to avoid IPC flooding, Electron only)
  useEffect(() => {
    if (!isElectron()) return;
    if (!tabsInitializedRef.current || tabs.length === 0) return;
    
    // Clear any pending save
    if (saveTabsTimeoutRef.current !== null) {
      window.clearTimeout(saveTabsTimeoutRef.current);
    }

    // Debounce persistence to avoid flooding IPC during rapid state changes
    // (e.g., streaming tokens, rapid tab switching)
    saveTabsTimeoutRef.current = window.setTimeout(() => {
      const tabsToStore = tabs.map(serializeTabForStorage);
      appStorage.setItem(StorageKeys.TABS, tabsToStore);
    }, 750);
  }, [tabs, serializeTabForStorage]);

  // Persist active tab ID to storage when it changes (Electron only)
  useEffect(() => {
    if (!isElectron()) return;
    if (!tabsInitializedRef.current || !activeTabId) return;
    appStorage.setItem(StorageKeys.ACTIVE_TAB_ID, activeTabId);
  }, [activeTabId]);

  // Update thinking mode when selectedModel changes to use model's default
  useEffect(() => {
    if (selectedModel?.thinking_config?.supported && selectedModel.thinking_config.default) {
      // Check if the currently selected thinkingMode is supported by the newly selected model
      // if so, return here, nothing to do, if not proceed with switching to the default for the model
      if (thinkingMode && selectedModel.thinking_config.presets[thinkingMode].enabled) {
        return;
      }
      setThinkingMode(selectedModel.thinking_config.default);
    }
  }, [selectedModel, thinkingMode]);

  // File-related state updates
  const removeFile = useCallback(
    (id) => {
      const newStatus = { ...uploadStatus };
      setFiles((prevFiles) => prevFiles.filter((f) => f.id !== id));
      if (Object.prototype.hasOwnProperty.call(newStatus, id)) {
        delete newStatus[id];
        setUploadStatus(newStatus);
      }
    },
    [uploadStatus]
  );

  const addFiles = useCallback((nFiles) => {
    const newFiles = nFiles.map((file) => ({
      file,
      src: URL.createObjectURL(file),
      id: Math.random().toString(36).substring(7),
    }));
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    const newStatus = {};
    newFiles.forEach((file) => {
      newStatus[file.id] = { status: 'loading' };
    });
    setUploadStatus((prev) => ({ ...prev, ...newStatus }));
  }, []);

  const resetFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const finishUpload = useCallback((id) => {
    setTimeout(() => {
      setUploadStatus((prev) => ({
        ...prev,
        [id]: { status: 'done' },
      }));
    }, 1000);
  }, []);

  // Basic UI handlers
  const onInputChange = useCallback((value) => setInput(value), []);
  const onSysPromptChange = useCallback((value) => setSysPrompt(value), []);
  const onMenuIsOpenChange = useCallback((value) => setMenuIsOpen(value), []);
  const onShowDeskChange = useCallback((value) => setShowDesk(value), []);

  const updateConversationInConversations = useCallback(
    (history, conversationId = null) => {
      if (conversationId === null) {
        setSelectedConversation((prev) => ({ ...prev, history }));
        return;
      }

      const currentFavorites = favoriteConversationsRef.current;
      const currentConversations = conversationsRef.current;
      const currentSelected = selectedConversationRef.current;

      const existingInFavorites = currentFavorites.find((c) => c.conversationId === conversationId);
      const existingInRegular = currentConversations.find((c) => c.conversationId === conversationId);
      const isSelected = currentSelected.conversationId === conversationId;

      const isFavorite = existingInFavorites || (isSelected && currentSelected.favorite);

      if (isFavorite) {
        if (existingInFavorites) {
          setFavoriteConversations((prevFavorites) => {
            return prevFavorites.map((conv) => (conv.conversationId === conversationId ? { ...conv, history } : conv));
          });
        }
      } else if (existingInRegular) {
        setConversations((prevConversations) => {
          return prevConversations.map((conv) => (conv.conversationId === conversationId ? { ...conv, history } : conv));
        });
      } else {
        setConversations((prevConversations) => [
          ...prevConversations,
          {
            conversationId,
            history,
            title: 'New Conversation',
            favorite: false,
            metadata: {},
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ]);
      }

      if (isSelected) {
        setSelectedConversation((prev) => ({
          ...prev,
          history,
        }));
      }
    },
    []
  );

  const removeFromConversations = useCallback((conversationId) => {
    if (!conversationId) return;
    setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
  }, []);

  const fetchConversationMessages = useCallback(async (conversationId: string): Promise<Message[]> => {
    if (!conversationId) return [];
    try {
      const messages = await apiClient.get<Message[]>(`/api/conversations/${conversationId}/messages`);
      const mapped = await Promise.all(messages.map(async (message): Promise<Message | null> => {
        const isEncrypted = (message as any).encrypted === true;

        if (message.role === Role.user) {
          let content = message.content || '';
          let metadata: any = message.metadata || {};
          if (isEncrypted) {
            if (isUnlocked) {
              try {
                content = await decrypt(message.content);
              } catch {
                content = '[Encrypted message]';
              }
              // Decrypt user metadata (images) if present
              if (metadata.encrypted) {
                try {
                  metadata = await decryptObject(metadata.encrypted);
                } catch {
                  metadata = {};
                }
              }
            } else {
              content = '[Encrypted message]';
              metadata = {};
            }
          } else if (metadata.encrypted) {
            // Non-encrypted: metadata.encrypted is a JSON string
            try {
              metadata = JSON.parse(metadata.encrypted);
            } catch {
              metadata = {};
            }
          }
          const userMessage: UserMessage = {
            id: message.id,
            conversation_id: message.conversation_id,
            role: message.role,
            content,
            created_at: message.created_at,
            message_parts: null,
            metadata,
          }
          return userMessage;
        } else if (message.role === Role.assistant) {
          let messageParts = message.message_parts || [];
          let metadata = message.metadata || {};
          if (isEncrypted) {
            if (isUnlocked) {
              try {
                const payload = await decryptObject<{ message_parts: any[]; metadata?: any }>(message.content);
                messageParts = payload.message_parts;
                metadata = payload.metadata || {};
              } catch {
                messageParts = [{ type: 'text', content: '[Encrypted message - sign in again to decrypt]' }];
              }
            } else {
              messageParts = [{ type: 'text', content: '[Encrypted message - sign in again to decrypt]' }];
            }
          } else if ((!messageParts || messageParts.length === 0) && message.content) {
            // Non-encrypted message stored via client-side path: content is a JSON string
            try {
              const payload = JSON.parse(message.content);
              if (payload.message_parts) {
                messageParts = payload.message_parts;
                metadata = payload.metadata || metadata;
              }
            } catch {
              // Not JSON — legacy format, use content as text
              messageParts = [{ type: 'text', content: message.content }];
            }
          }
          const sanitizedParts = Array.isArray(messageParts)
            ? messageParts.filter((p: any) => VALID_ASSISTANT_PART_TYPES.has(p?.type))
            : [];
          const assistantMessage: AssistantMessage = {
            id: message.id,
            conversation_id: message.conversation_id,
            role: message.role,
            content: null,
            created_at: message.created_at,
            message_parts: sanitizedParts,
            metadata: metadata as any,
          }
          return assistantMessage;
        }
        return null;
      }));
      const result = mapped.filter((message): message is Message => message !== null);

      // Mark trailing user message as orphaned (interrupted interaction)
      if (result.length > 0 && result[result.length - 1].role === 'user') {
        const last = result[result.length - 1];
        result[result.length - 1] = { ...last, metadata: { ...last.metadata, orphaned: true } };
      }

      return result;
    } catch (error) {
      console.error(`Error fetching messages for conversation ${conversationId}:`, error);
      return [];
    }
  }, [isUnlocked, decrypt, decryptObject]);

  const selectConversation = useCallback(
    async (conversationId) => {
      const conversation =
        conversationsRef.current.find((c) => c.conversationId === conversationId) ||
        favoriteConversationsRef.current.find((c) => c.conversationId === conversationId);

      if (isElectron()) {
        // Tab mode: reuse existing tab or create a new one
        const existingTab = tabsRef.current.find(t => t.conversationId === conversationId);
        
        if (existingTab) {
          switchTab(existingTab.id);
          return;
        }

        const tabId = createTab(conversationId);

        if (authSession?.access_token && conversationId) {
          try {
            const fetchedMessages = await fetchConversationMessages(conversationId);
            updateTab(tabId, { 
              messages: fetchedMessages,
              title: conversation?.title || 'New Chat',
              selectedModelId: conversation?.metadata?.model_id || null,
            });
            
            updateConversationInConversations(fetchedMessages, conversationId);
            
            const lastMsg = fetchedMessages[fetchedMessages.length - 1];
            updateTab(tabId, { activeConversationTokenCount: lastMsg?.metadata?.usage_log || 0 });
          } catch (error) {
            console.error('Error loading conversation messages:', error);
          }
        }
        return;
      }

      // Browser: use legacy global state
      if (authSession?.access_token && conversationId) {
        try {
          const fetchedMessages = await fetchConversationMessages(conversationId);
          setMessages(fetchedMessages);
          updateConversationInConversations(fetchedMessages, conversationId);
          
          const lastMsg = fetchedMessages[fetchedMessages.length - 1];
          setActiveConversationTokenCount(lastMsg?.metadata?.usage_log || 0);
        } catch (error) {
          console.error('Error loading conversation messages:', error);
        }
      }

      if (conversationId) {
        setSelectedConversation(conversation || { conversationId, history: [] });
      } else {
        setSelectedConversation({ conversationId: null, history: [] });
      }
    },
    [authSession, fetchConversationMessages, updateConversationInConversations, createTab, switchTab, updateTab]
  );

  const selectAgent = useCallback(() => ({ id: 1, name: 'Test' }), []);

  // Clear the current conversation (start a new blank chat)
  const clearConversation = useCallback(() => {
    setSelectedConversation({ conversationId: null, history: [] });
    setMessages([]);
    setInput('');
    setFiles([]);
  }, []);

  const startNewConversation = useCallback(() => {
    if (isElectron()) {
      const favorite = favoriteModelId
        ? enabledModels.find(m => m.id === favoriteModelId)
        : null;
      const modelId = favorite?.id || (enabledModels.length > 0 ? enabledModels[Math.floor(Math.random() * enabledModels.length)]?.id : null);
      createTab(null, modelId);
      navigate('/chat');
      return;
    }

    // Browser: use legacy global state
    setSelectedConversation({ conversationId: null, history: [] });
    setMessages([]);
    setInput('');
    setFiles([]);
    
    const favorite = favoriteModelId
      ? enabledModels.find(m => m.id === favoriteModelId)
      : null;
    
    if (favorite) {
      _setSelectedModel(favorite);
    } else if (enabledModels.length > 0) {
      _setSelectedModel(enabledModels[Math.floor(Math.random() * enabledModels.length)]);
    }
    
    isNewConversationIntent.current = true;
    navigate('/chat');
  }, [navigate, favoriteModelId, enabledModels, createTab]);

  const setSearchModal = useCallback(() => setSearchModalActive((prev) => !prev), []);

  // Auth & API keys
  const onAuthSessionUpdate = useCallback((session) => setAuthSession(session), []);

  const updateProfile = useCallback(
    async (updatedProfile) => {
      try {
        const data = await apiClient.put('/api/profile', {
          first_name: updatedProfile.firstName,
          last_name: updatedProfile.lastName,
          email: updatedProfile.email,
        });

        setProfile(data);
        profileFetchedRef.current = true;
      } catch (err) {
        console.error(err.message);
      }
    },
    []
  );

  const updateApiKeys = useCallback(
    async (keys) => {
      try {
        const data = await apiClient.put('/api/user-settings', {
          openai_apikey: keys.openAIApiKey,
          anthropic_apikey: keys.anthropicApiKey,
          gemini_apikey: keys.geminiApiKey,
          xai_apikey: keys.xaiApiKey,
          openrouter_apikey: keys.openrouterApiKey,
        });

        onApiKeysUpdate(data.anthropic_apikey, data.openai_apikey, data.gemini_apikey, data.xai_apikey, data.openrouter_apikey);

        await fetchModels();

        apikeysFetchedRef.current = true;
      } catch (err) {
        console.error(err.message);
      }
    },
    [onApiKeysUpdate, fetchModels]
  );

  const generateMonthlyReport = useCallback(async (testMode = false, month = null, year = null) => {
    try {
      let endpoint = testMode ? '/api/usage-logs?test_mode=true' : '/api/usage-logs';
      
      // Add month and year parameters if provided
      const params = new URLSearchParams();
      if (testMode) params.append('test_mode', 'true');
      if (month !== null) params.append('month', month);
      if (year !== null) params.append('year', year);
      
      if (params.toString()) {
        endpoint = `/api/usage-logs?${params.toString()}`;
      }
      
      const data = await apiClient.get(endpoint);
      return data;
    } catch (err) {
      console.error('Error fetching usage data:', err.message);
    }
  }, []);


  // Shortcut element refs
  const mainInputRef = useRef(null);
  const searchModalInputRef = useRef(null);
  const modelSelectorRef = useRef(null);
  const fileInputRef = useRef(null);
  const thinkingModeSelectorRef = useRef(null);

  // Focus the main input
  const focusMainInput = useCallback(() => {
    if (!mainInputRef.current) {
      if (import.meta.env.DEV) console.warn('[AppContext] mainInputRef not attached');
      return;
    }
    mainInputRef.current.focus();
  }, []);

  // Toggle model selector
  const toggleModelSelector = useCallback(() => {
    if (!modelSelectorRef.current?.toggle) {
      if (import.meta.env.DEV) console.warn('[AppContext] modelSelectorRef not attached or no toggle()');
      return;
    }
    modelSelectorRef.current.toggle();
  }, []);

  // Open file input dialog
  const openFileInput = useCallback(() => {
    if (!fileInputRef.current) {
      if (import.meta.env.DEV) console.warn('[AppContext] fileInputRef not attached');
      return;
    }
    fileInputRef.current.click();
  }, []);

  // Open thinking mode selector
  const openThinkingModeSelector = useCallback(() => {
    if (!thinkingModeSelectorRef.current?.toggle) {
      if (import.meta.env.DEV) console.warn('[AppContext] thinkingModeSelectorRef not attached or no toggle()');
      return;
    }
    thinkingModeSelectorRef.current.toggle();
  }, []);

  // Start a new conversation
  const createNewConversation = useCallback(() => {
    startNewConversation();
  }, [startNewConversation]);

  // Toggle sidebar
  const toggleSidebar = useCallback(() => {
    setMenuIsOpen((prev) => !prev);
  }, []);

  // Confirmation Modal Functions
  const showConfirmation = useCallback(
    ({ title, message, confirmText = 'Confirm', cancelText = 'Cancel', variant = 'default', onConfirm, onCancel }) => {
      return new Promise((resolve) => {
        setConfirmationModal({
          isOpen: true,
          title,
          message,
          confirmText,
          cancelText,
          variant,
          isLoading: false,
          onConfirm: async () => {
            try {
              setConfirmationModal((prev) => ({ ...prev, isLoading: true }));
              if (onConfirm) {
                await onConfirm();
              }
              resolve(true);
            } catch (error) {
              console.error('Confirmation action failed:', error);
              resolve(false);
            } finally {
              setConfirmationModal((prev) => ({ ...prev, isOpen: false, isLoading: false }));
            }
          },
          onCancel: () => {
            if (onCancel) {
              onCancel();
            }
            setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
            resolve(false);
          },
        });
      });
    },
    []
  );

  const hideConfirmation = useCallback(() => {
    setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Messages context value (separate to avoid re-rendering all consumers during streaming)
  const messagesValue = useMemo<MessagesContextType>(
    () => ({ messages, setMessages }), 
    [messages],
  );

  // Derived auth state flags
  const isAuthenticated = !!authSession;
  const isAuthKnown = !authLoading;
  const isAppInitializing = isAuthenticated && initializingUI;
  const isAppReady = isAuthenticated && !initializingUI;

  // Context value
  const value = useMemo(
    () => ({
      // Supabase
      supabaseClient: supabase,

      // Models
      models, // All available models (filtered by API keys)
      enabledModels, // Filtered by user's enabled_model_ids
      selectedModel,
      setSelectedModel,
      enabledModelIds,
      favoriteModelId,
      setEnabledModelIds,
      setFavoriteModelId,

      // System prompt
      systemPrompt,
      setSystemPrompt,

      // Web search preference
      preferAnthropicWebSearch,
      setPreferAnthropicWebSearch,

      // Auth
      authSession,
      authLoading,
      isAuthenticated,
      isAuthKnown,
      isAppInitializing,
      isAppReady,

      // API Keys / Profile
      apiKeys,
      profile,
      setProfile,

      // UI and chat state
      thinkingMode,
      setThinkingMode,
      menuIsOpen,
      searchModalActive,
      input,
      showDesk,
      initializingUI,

      // Files
      files,
      uploadStatus,
      addFiles,
      removeFile,
      resetFiles,
      finishUpload,

      // Conversations
      conversations,
      favoriteConversations,
      selectedConversation,
      conversationsLoading,
      updateTitle,
      updateConversationInConversations,
      removeFromConversations,
      selectConversation,
      deleteConversation,
      deleteAllConversations,
      favoriteConversation,
      setConversations,
      setFavoriteConversations,
      setSelectedConversation,
      syncConversationsWithServer,

      // Tabs
      tabs,
      activeTabId,
      getActiveTab,
      createTab,
      closeTab,
      switchTab,
      updateTab,
      closeOtherTabs,
      closeAllTabs,
      reorderTabs,
      renameTab,

      // Navigation / controls
      selectAgent,
      startNewConversation,
      clearConversation,
      createNewConversation,
      toggleSidebar,

      // Message fetching
      fetchConversationMessages,

      // UI handlers
      onInputChange,
      onSysPromptChange,
      onMenuIsOpenChange,
      onShowDeskChange,
      setSearchModal,

      // Auth & API keys
      onAuthSessionUpdate,
      onApiKeysUpdate,
      updateProfile,
      updateApiKeys,

      // Search
      searchConversations,

      // Loading state
      loadingConversation,
      isGenerating,
      setIsGenerating,

      // Reports
      generateMonthlyReport,

      // Token usage
      activeConversationTokenCount,
      setActiveConversationTokenCount,

      // Refs for UI controls
      mainInputRef,
      searchModalInputRef,
      modelSelectorRef,
      fileInputRef,
      thinkingModeSelectorRef,

      // Shortcuts
      focusMainInput,
      toggleModelSelector,
      openFileInput,
      openThinkingModeSelector,

      // New conversation intent
      isNewConversationIntent,

      // Confirmation modal
      confirmationModal,
      showConfirmation,
      hideConfirmation,

      // Theme
      theme: themeControls.theme,
      setTheme: themeControls.setTheme,
      toggleTheme: themeControls.toggleTheme,
      isDark: themeControls.isDark,
      isLight: themeControls.isLight,
      isSystem: themeControls.isSystem,
      actualTheme: themeControls.actualTheme,
    }),
    [
      // Supabase is a module singleton (stable)

      // Models
      models,
      enabledModels,
      selectedModel,
      enabledModelIds,
      favoriteModelId,

      // System prompt
      systemPrompt,
      setSystemPrompt,

      // Auth
      authSession,
      authLoading,
      isAuthenticated,
      isAuthKnown,
      isAppInitializing,
      isAppReady,

      // API Keys / Profile
      apiKeys,
      profile,
      setProfile,

      // UI and chat state
      thinkingMode,
      menuIsOpen,
      searchModalActive,
      input,
      showDesk,
      initializingUI,

      // Files
      files,
      uploadStatus,
      addFiles,
      removeFile,
      resetFiles,
      finishUpload,

      // Conversations
      conversations,
      favoriteConversations,
      selectedConversation,
      conversationsLoading,
      updateTitle,
      updateConversationInConversations,
      removeFromConversations,
      selectConversation,
      deleteConversation,
      deleteAllConversations,
      favoriteConversation,
      setConversations,
      setFavoriteConversations,
      setSelectedConversation,
      syncConversationsWithServer,

      // Tabs
      tabs,
      activeTabId,
      getActiveTab,
      createTab,
      closeTab,
      switchTab,
      updateTab,
      closeOtherTabs,
      closeAllTabs,
      reorderTabs,
      renameTab,

      // Navigation / controls
      selectAgent,
      startNewConversation,
      clearConversation,
      createNewConversation,
      toggleSidebar,

      // Message fetching
      fetchConversationMessages,

      // UI handlers
      onInputChange,
      onSysPromptChange,
      onMenuIsOpenChange,
      onShowDeskChange,
      setSearchModal,

      // Auth & API keys
      onAuthSessionUpdate,
      onApiKeysUpdate,
      updateProfile,
      updateApiKeys,

      // Search
      searchConversations,

      // Loading state
      loadingConversation,
      isGenerating,
      setIsGenerating,

      // Reports
      generateMonthlyReport,

      // Token usage
      activeConversationTokenCount,
      setActiveConversationTokenCount,

      // Refs and shortcuts
      mainInputRef,
      searchModalInputRef,
      modelSelectorRef,
      fileInputRef,
      thinkingModeSelectorRef,
      focusMainInput,
      toggleModelSelector,
      openFileInput,
      openThinkingModeSelector,

      // New conversation intent
      isNewConversationIntent,

      // Confirmation modal
      confirmationModal,
      showConfirmation,
      hideConfirmation,

      // Theme
      themeControls.theme,
      themeControls.setTheme,
      themeControls.toggleTheme,
      themeControls.isDark,
      themeControls.isLight,
      themeControls.isSystem,
      themeControls.actualTheme,
    ]
  );

  return (
    <AppContext.Provider value={value}>
      <MessagesContext.Provider value={messagesValue}>
        {children}
      </MessagesContext.Provider>
    </AppContext.Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
